/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import * as faceapi from 'face-api.js';
import { t } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';
import { useNav } from 'src/onboarding/hooks/nav';
import { useStore } from 'src/onboarding/store/store';
import { FileService } from 'src/onboarding/services/file.service';
import { AiService } from 'src/onboarding/services/ai.service';
import { loadFaceApiModels } from 'src/onboarding/utils/faceApiPreload';
import { Screen, Title, Subtitle } from './Screen';
import { PrimaryButton } from './PrimaryButton';
import { trackFromGender, img, icon } from './hairstyle';
import styles from './PhotoCapture.module.scss';

// Shared camera stream across both selfie screens: permission is requested once on the
// front step; the side step reuses the same live stream, so the browser never re-prompts.
// Released by the analysis screen (stopSharedCamera) once selfies are done.
let sharedStream: MediaStream | null = null;
const hasLiveCamera = () => !!sharedStream && sharedStream.getVideoTracks().some((tr) => tr.readyState === 'live');
export const stopSharedCamera = () => {
  sharedStream?.getTracks().forEach((tr) => tr.stop());
  sharedStream = null;
};

type Props = {
  page: string;
  progress: number;
  ns: string;
  exampleName: string;
  field: string;
  setter: string;
  urlSetter?: string;
  outline: 'front' | 'side';
  step?: number;
  totalSteps?: number;
  prevField?: string;
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

/**
 * Face check for uploaded files. The camera path is already gated by on-device
 * detection; a photo from the library is not, so it goes through the same
 * /ai/face-landmarks endpoint the face-scan screens use. Only the yes/no is
 * needed here — this funnel never draws the landmarks.
 *
 * A photo the service rejects (no landmarks, or a 4xx) fails the check. A
 * service that is down (5xx / no response) lets the photo through: a broken
 * AI backend must not block the funnel.
 */
const hasFaceOnPhoto = async (file: File): Promise<boolean> => {
  try {
    const response: any = await AiService.faceLandmarks(file);
    return (response?.data?.landmarks?.length ?? 0) > 0;
  } catch (error: any) {
    const status = error?.response?.status;
    const isPhotoRejected = typeof status === 'number' && status >= 400 && status < 500;

    Analytics.trackEvent('face_landmarks', isPhotoRejected ? 'not_found' : 'error', {
      status: status ?? 'network',
    });

    return !isPhotoRejected;
  }
};

const dataUrlToFile = (dataUrl: string, name: string): File => {
  const [meta, b64] = dataUrl.split(',');
  const mime = (meta.match(/:(.*?);/) || [])[1] || 'image/jpeg';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: mime });
};

// Clean head icon — uses the designer's gender-specific custom icon from hairstyle/icons/
// (head-front-man / head-side-woman / …), then a generic head-front / head-side, then a
// drawn silhouette fallback. No arrows.
const HeadIcon: React.FC<{ variant: 'front' | 'side'; className?: string }> = ({ variant, className }) => {
  const gender = useStore((s: any) => s.hairstyleGender);
  const g = gender === 'female' ? 'woman' : 'man'; // default / "prefer not to say" → man set
  const custom = icon(`head-${variant}-${g}`) || icon(`head-${variant}`);
  if (custom) return <img src={custom} alt="" className={className} aria-hidden="true" />;
  return variant === 'side' ? (
    <svg className={className} viewBox="0 0 64 72" fill="none" aria-hidden="true">
      <path
        d="M47 6 C34 6 24 15 22 28 C21 33 16 35 15 40 C14 43 18 44 19 46 C19 49 16 50 17 53 C18 58 25 62 33 62 L49 62"
        stroke="#ff6aa0" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg className={className} viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <circle cx="32" cy="23" r="13" stroke="#ff6aa0" strokeWidth="2.6" />
      <path d="M11 57 c0-13 9-21 21-21 s21 8 21 21" stroke="#ff6aa0" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
};

/** Selfie capture: live camera (getUserMedia) or library, auto-advances once a photo is taken. */
export const PhotoCapture: React.FC<Props> = ({
  page, progress, ns, exampleName, field, setter, urlSetter, outline, step, totalSteps, prevField,
}) => {
  const { onNext, onPrev, currentIndex } = useNav();
  const gender = useStore((s: any) => s.hairstyleGender);
  const photo = useStore((s: any) => s[field]) as string | undefined;
  const setPhoto = useStore((s: any) => s[setter]) as (v: string) => void;
  const setUrl = useStore((s: any) => (urlSetter ? s[urlSetter] : undefined)) as ((v: string) => void) | undefined;
  const prevPhoto = useStore((s: any) => (prevField ? s[prevField] : undefined)) as string | undefined;

  const [mode, setMode] = useState<'idle' | 'camera' | 'captured'>('idle');
  // Uploaded photo is being checked for a face; the buttons wait for the answer.
  const [checking, setChecking] = useState(false);
  // Set when the check came back without a face — the photo is not accepted.
  const [noFace, setNoFace] = useState(false);
  // On-device face-detection gate: the shutter unlocks only once a face is properly framed.
  const [faceReady, setFaceReady] = useState(false);
  // Keep the preview hidden until the first real frame lands (see effect below).
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const libRef = useRef<HTMLInputElement>(null);

  const example = img(trackFromGender(gender), exampleName);

  // Force Chrome's compositor to re-rasterize the video box. On the first frames of a
  // camera stream Chrome paints the content letterboxed/centred, IGNORING object-fit: cover,
  // until some relayout of the element happens — this display toggle is that relayout.
  // display flips back within the same task, so nothing ever actually paints as hidden.
  const nudgeRepaint = () => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.style.display;
    v.style.display = 'none';
    void v.offsetHeight; // flush layout between the two writes
    v.style.display = d;
  };

  // Attach the shared stream once the <video> is mounted in camera mode, and reveal it only
  // after the first real frame renders. Chrome fires loadedmetadata/playing while videoWidth
  // is still 0 and paints the frame small & centred (no object-fit) until a relayout — that's
  // the "size jumps from small to full" flash. Waiting for requestVideoFrameCallback + a
  // repaint nudge makes it fill the frame from the very first painted frame.
  useEffect(() => {
    if (mode !== 'camera') {
      setVideoReady(false);
      return;
    }
    const v = videoRef.current;
    if (!v || !sharedStream) return;
    v.srcObject = sharedStream;
    v.play().catch(() => {});

    let done = false;
    let safety: number | undefined;
    const commit = () => {
      if (done) return;
      done = true;
      nudgeRepaint();
      setVideoReady(true);
      window.setTimeout(nudgeRepaint, 300); // once more after the fade-in lands
    };
    const rvfc = (v as any).requestVideoFrameCallback;
    if (typeof rvfc === 'function') {
      rvfc.call(v, () => commit());
      safety = window.setTimeout(commit, 1000); // don't hang if no frame callback fires
    } else {
      safety = window.setTimeout(commit, 300); // Safari path: reveal shortly after play()
    }
    return () => {
      if (safety) clearTimeout(safety);
    };
  }, [mode]);

  // Face-detection gate (face-api.js tinyFaceDetector, same models as face-scan-id).
  // While the camera is open we watch the live video and only enable the shutter once a
  // face is properly framed — front: centered + large enough; side/profile: a softer
  // presence check at a lower threshold (frontal detection is unreliable at true profile).
  // Library upload bypasses the gate; if the models fail to load we unlock the shutter so
  // a model-load failure never traps the user.
  useEffect(() => {
    if (mode !== 'camera') {
      setFaceReady(false);
      return;
    }
    let cancelled = false;
    let interval: number | undefined;

    (async () => {
      // resolves false when the weights could not be fetched — running the
      // detector then would throw on every frame and never unlock the shutter
      const modelsOk = await loadFaceApiModels();
      if (cancelled) return;
      if (!modelsOk) {
        setFaceReady(true); // fallback: don't block checkout on a model-load failure
        return;
      }

      const opts = new faceapi.TinyFaceDetectorOptions({
        inputSize: 224,
        scoreThreshold: outline === 'side' ? 0.3 : 0.45,
      });

      interval = window.setInterval(async () => {
        const video = videoRef.current;
        if (!video || video.readyState < 2 || !video.videoWidth) return;

        let det: any;
        try {
          det = await faceapi.detectSingleFace(video, opts);
        } catch {
          return;
        }
        if (cancelled) return;

        if (!det?.box) {
          setFaceReady(false);
          return;
        }

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const { x, y, width, height } = det.box;

        // face must fill enough of the frame (relaxed for the profile shot)
        const minSize = (outline === 'side' ? 0.18 : 0.24) * vw;
        if (width < minSize) {
          setFaceReady(false);
          return;
        }

        // front shot additionally requires the face roughly centered
        if (outline === 'front') {
          const fx = x + width / 2;
          const fy = y + height / 2;
          const centered = fx > vw * 0.3 && fx < vw * 0.7 && fy > vh * 0.18 && fy < vh * 0.85;
          if (!centered) {
            setFaceReady(false);
            return;
          }
        }

        setFaceReady(true);
      }, 180);
    })();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [mode, outline]);

  const finalize = (base64: string, file: File, source: string) => {
    setPhoto(base64);
    setMode('captured');
    Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', { page, source });
    if (setUrl) {
      FileService.createImage(file)
        .then((res: any) => { if (res?.data?.url) setUrl(res.data.url); })
        .catch(() => {});
    }
    setTimeout(() => onNext(), 550);
  };

  const startCamera = async () => {
    try {
      if (!hasLiveCamera()) {
        Analytics.trackEvent(`step${currentIndex + 1}`, 'cameraRequest', { page });
        sharedStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      }
      setMode('camera');
    } catch (e) {
      Analytics.trackEvent(`step${currentIndex + 1}`, 'cameraDenied', { page });
      libRef.current?.click(); // no camera / denied → library fallback
    }
  };

  const shoot = () => {
    if (!faceReady) return; // shutter is gated on a detected face
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 720;
    const h = video.videoHeight || 960;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    const base64 = canvas.toDataURL('image/jpeg', 0.9);
    // keep the shared stream alive for the next selfie step; released on the analysis screen
    finalize(base64, dataUrlToFile(base64, 'selfie.jpg'), 'camera');
  };

  const cancelCamera = () => setMode('idle'); // stream stays alive (reused / released later)

  const onLibrary = async (input: HTMLInputElement) => {
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    setNoFace(false);
    setChecking(true);

    try {
      const base64 = await readAsDataUrl(file);

      if (!(await hasFaceOnPhoto(file))) {
        setNoFace(true);
        Analytics.trackEvent(`step${currentIndex + 1}`, 'photoRejected', { page });
        return;
      }

      finalize(base64, file, 'library');
    } catch {
      setNoFace(true);
    } finally {
      setChecking(false);
    }
  };

  const stepLabel = step && totalSteps
    ? t('hairstyleCamera.step').replace('{n}', String(step)).replace('{m}', String(totalSteps))
    : '';

  return (
    <Screen
      progress={progress}
      hideBack={currentIndex === 0 || mode === 'camera'}
      onBack={onPrev}
      footer={
        <div className={styles.actions}>
          {mode === 'camera' ? (
            <>
              <PrimaryButton sticky={false} disabled={!faceReady} onClick={shoot}>{t('hairstyleCamera.capture')}</PrimaryButton>
              <PrimaryButton sticky={false} variant="ghost" onClick={cancelCamera}>{t('hairstyleCamera.cancel')}</PrimaryButton>
            </>
          ) : (
            <>
              <PrimaryButton sticky={false} disabled={checking} onClick={startCamera}>{t(`${ns}.take`)}</PrimaryButton>
              <PrimaryButton
                sticky={false}
                variant="ghost"
                disabled={checking}
                onClick={() => libRef.current?.click()}
              >
                {checking ? t('hairstyleCamera.checking') : t(`${ns}.upload`)}
              </PrimaryButton>
            </>
          )}
        </div>
      }
    >
      {step && totalSteps && (
        <div className={styles.steps}>
          <span className={styles.stepLabel}>{stepLabel}</span>
          <div className={styles.dots}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span key={i} className={`${styles.dot} ${i < step - 1 ? styles.dotDone : ''} ${i === step - 1 ? styles.dotActive : ''}`} />
            ))}
          </div>
        </div>
      )}

      <Title>{t(`${ns}.title`)}</Title>
      <Subtitle>{t(`${ns}.subtitle`)}</Subtitle>

      {prevPhoto && (
        <div className={styles.flow}>
          <div className={styles.thumbDone}>
            <img src={prevPhoto} alt="" />
            <span className={styles.thumbCheck}>✓</span>
          </div>
          <span className={styles.flowArrow}>→</span>
          <div className={styles.thumbNext}><HeadIcon variant={outline} className={styles.thumbHead} /></div>
        </div>
      )}

      <div className={`${styles.frame} ${outline === 'side' ? styles.side : ''} ${mode === 'camera' && faceReady ? styles.frameReady : ''}`}>
        <span className={`${styles.corner} ${styles.c1}`} />
        <span className={`${styles.corner} ${styles.c2}`} />
        <span className={`${styles.corner} ${styles.c3}`} />
        <span className={`${styles.corner} ${styles.c4}`} />
        {mode === 'camera' && (
          <span className={`${styles.faceHint} ${faceReady ? styles.faceHintOk : ''}`}>
            {faceReady ? t('hairstyleCamera.detected') : t('hairstyleCamera.detecting')}
          </span>
        )}
        {mode === 'camera' ? (
          <video
            ref={videoRef}
            className={styles.video}
            style={{ opacity: videoReady ? 1 : 0 }}
            autoPlay
            playsInline
            muted
          />
        ) : photo && mode === 'captured' ? (
          <img src={photo} alt="" className={styles.preview} />
        ) : example ? (
          <img src={example} alt="" className={styles.example} />
        ) : (
          <HeadIcon variant={outline} className={styles.headIcon} />
        )}
      </div>

      {noFace ? (
        <p className={styles.error} role="alert">{t('hairstyleCamera.noFace')}</p>
      ) : (
        <p className={styles.hint}>{t(`${ns}.hint`)}</p>
      )}

      <input ref={libRef} type="file" accept="image/*" hidden onChange={(e) => onLibrary(e.target)} />
    </Screen>
  );
};

export default PhotoCapture;
